import {
  createSimulatedEvent,
  type CharacterSnapshot,
  type ModoVivoEvent,
} from '@modo-vivo/contracts';
import { CharacterController, EventProcessor } from '@modo-vivo/engine/runtime';
import { ENTRANCE_POINT, WAITER_HOME } from './layout.js';
import { pathFromEntranceToTable, pathFromTableToEntrance } from './paths.js';

export type TableStatus =
  | 'libre'
  | 'reservada'
  | 'ocupada'
  | 'cuenta_solicitada'
  | 'limpieza';

export interface TableState {
  number: number;
  status: TableStatus;
  customers: string[];
  waiterId?: string;
  orderId?: string;
  orderItems: Array<{ name: string; quantity: number; unitPrice: number }>;
  consumptionTotal: number;
  occupiedSince?: number;
}

export interface KitchenOrder {
  orderId: string;
  tableNumber: number;
  status: 'pendiente' | 'en_preparacion' | 'terminado' | 'entregado';
  items: Array<{ name: string; quantity: number }>;
  createdAt: number;
  startedAt?: number;
  finishedAt?: number;
}

export interface RegisterState {
  salesToday: number;
  operationCount: number;
  totalCollected: number;
  open: boolean;
  payments: Record<string, number>;
}

export interface StorageItem {
  sku: string;
  name: string;
  quantity: number;
  minStock: number;
}

export interface EmployeeState {
  id: string;
  name: string;
  role: string;
  status: string;
  activity: string;
}

export interface RestaurantSnapshot {
  simulated: true;
  tables: TableState[];
  register: RegisterState;
  kitchen: KitchenOrder[];
  storage: StorageItem[];
  employees: EmployeeState[];
  characters: CharacterSnapshot[];
  eventLog: ModoVivoEvent[];
  lastSupplierDelivery?: { at: number; supplierName: string; items: StorageItem[] };
}

type Listener = (snap: RestaurantSnapshot) => void;

const CUSTOMER_NAMES = ['Ana', 'Bruno', 'Carla', 'Diego', 'Elena', 'Facundo', 'Gina', 'Hugo'];

export class RestaurantSimulator {
  private tables: TableState[] = Array.from({ length: 10 }, (_, i) => ({
    number: i + 1,
    status: 'libre',
    customers: [],
    orderItems: [],
    consumptionTotal: 0,
  }));

  private register: RegisterState = {
    salesToday: 0,
    operationCount: 0,
    totalCollected: 0,
    open: true,
    payments: { efectivo: 0, tarjeta: 0, transferencia: 0, qr: 0 },
  };

  private kitchen: KitchenOrder[] = [];
  private storage: StorageItem[] = [
    { sku: 'CAR-001', name: 'Carne vacuna (kg)', quantity: 42, minStock: 15 },
    { sku: 'VER-010', name: 'Verduras mixtas', quantity: 28, minStock: 12 },
    { sku: 'BEB-003', name: 'Gaseosas (caja)', quantity: 18, minStock: 8 },
    { sku: 'PAN-002', name: 'Pan artesanal', quantity: 9, minStock: 10 },
  ];

  private employees: EmployeeState[] = [
    {
      id: 'waiter-1',
      name: 'Lucas',
      role: 'Mozo',
      status: 'disponible',
      activity: 'En salón',
    },
    {
      id: 'waiter-2',
      name: 'Valentina',
      role: 'Moza',
      status: 'disponible',
      activity: 'En barra',
    },
  ];

  private controllers = new Map<string, CharacterController>();
  private eventLog: ModoVivoEvent[] = [];
  private processor = new EventProcessor((evt: ModoVivoEvent) => this.eventLog.push(evt));
  private listeners = new Set<Listener>();
  private customerCounter = 0;
  private orderCounter = 0;
  private arrivalQueue: number[] = [1, 2, 3, 4];
  private lastSupplierDelivery?: RestaurantSnapshot['lastSupplierDelivery'];

  constructor() {
    this.bootstrapStaff();
    this.wireProcessor();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  snapshot(): RestaurantSnapshot {
    return {
      simulated: true,
      tables: this.tables.map((t) => ({ ...t, customers: [...t.customers], orderItems: [...t.orderItems] })),
      register: { ...this.register, payments: { ...this.register.payments } },
      kitchen: this.kitchen.map((k) => ({ ...k, items: [...k.items] })),
      storage: this.storage.map((s) => ({ ...s })),
      employees: this.employees.map((e) => ({ ...e })),
      characters: [...this.controllers.values()].map((c) => c.snapshot()),
      eventLog: [...this.eventLog],
      lastSupplierDelivery: this.lastSupplierDelivery,
    };
  }

  dispatch(event: ModoVivoEvent): void {
    this.processor.enqueue(event);
    this.emit();
  }

  /** Acciones de demostración (simulador UI). */
  demoCustomerArrival(): void {
    const tableNumber = this.arrivalQueue.shift() ?? this.findFreeTable();
    if (!tableNumber) {
      return;
    }
    this.customerCounter += 1;
    const customerId = `customer-${this.customerCounter}`;
    const name = CUSTOMER_NAMES[(this.customerCounter - 1) % CUSTOMER_NAMES.length];
    const ctrl = new CharacterController({
      id: customerId,
      type: 'customer',
      displayName: name,
      position: { ...ENTRANCE_POINT },
      state: 'walking',
      animation: 'walk',
    });
    this.controllers.set(customerId, ctrl);
    this.dispatch(
      createSimulatedEvent('customer.arrival', {
        customerId,
        partySize: 1,
        targetTableNumber: tableNumber,
      }),
    );
  }

  demoOpenTable(tableNumber: number): void {
    this.dispatch(createSimulatedEvent('table.open', { tableNumber }));
  }

  demoAssignWaiter(tableNumber: number, waiterId = 'waiter-1'): void {
    this.dispatch(createSimulatedEvent('table.assign', { tableNumber, waiterId }));
  }

  demoRegisterOrder(tableNumber: number): void {
    const items = [
      { name: 'Bife de chorizo', quantity: 1, unitPrice: 18500 },
      { name: 'Ensalada César', quantity: 1, unitPrice: 7200 },
      { name: 'Agua sin gas', quantity: 2, unitPrice: 1800 },
    ];
    this.dispatch(createSimulatedEvent('order.register', { tableNumber, items }));
  }

  demoSendKitchen(tableNumber: number): void {
    const table = this.getTable(tableNumber);
    if (!table?.orderId) {
      return;
    }
    this.dispatch(
      createSimulatedEvent('order.send_kitchen', {
        tableNumber,
        orderId: table.orderId,
      }),
    );
  }

  demoPreparing(orderId: string): void {
    this.dispatch(createSimulatedEvent('order.preparing', { orderId }));
  }

  demoReady(orderId: string): void {
    this.dispatch(createSimulatedEvent('order.ready', { orderId }));
  }

  demoDeliver(orderId: string, waiterId = 'waiter-1'): void {
    this.dispatch(createSimulatedEvent('order.deliver', { orderId, waiterId }));
  }

  demoBill(tableNumber: number): void {
    this.dispatch(createSimulatedEvent('bill.request', { tableNumber }));
  }

  demoPayment(tableNumber: number): void {
    const table = this.getTable(tableNumber);
    if (!table) {
      return;
    }
    this.dispatch(
      createSimulatedEvent('payment.collect', {
        tableNumber,
        amount: table.consumptionTotal,
        method: 'tarjeta' as const,
      }),
    );
  }

  demoCustomersLeave(tableNumber: number): void {
    const table = this.getTable(tableNumber);
    if (!table) {
      return;
    }
    this.dispatch(
      createSimulatedEvent('customer.leave', {
        tableNumber,
        customerIds: [...table.customers],
      }),
    );
  }

  demoReleaseTable(tableNumber: number): void {
    this.dispatch(createSimulatedEvent('table.release', { tableNumber }));
  }

  demoSupplier(): void {
    const items = [
      { sku: 'CAR-001', name: 'Carne vacuna (kg)', quantity: 12 },
      { sku: 'BEB-003', name: 'Gaseosas (caja)', quantity: 6 },
    ];
    this.dispatch(
      createSimulatedEvent('supplier.receive', {
        supplierName: 'Distribuidora Sur (ficticia)',
        items,
      }),
    );
  }

  demoStockUpdate(): void {
    this.dispatch(createSimulatedEvent('stock.update', { sku: 'PAN-002', quantityDelta: 15 }));
  }

  tick(deltaMs: number): void {
    for (const ctrl of this.controllers.values()) {
      ctrl.update(deltaMs);
    }
    this.emit();
  }

  private wireProcessor(): void {
    this.processor.on('customer.arrival', (e) => {
      if (e.type !== 'customer.arrival') {
        return;
      }
      const { customerId, targetTableNumber } = e.payload;
      const tableNumber = targetTableNumber ?? this.findFreeTable();
      if (!tableNumber) {
        return;
      }
      const ctrl = this.controllers.get(customerId);
      const path = pathFromEntranceToTable(tableNumber);
      ctrl?.followPath(path, {
        speed: 2.8,
        onComplete: () => {
          ctrl.setState('seated', 'sit');
          const table = this.getTable(tableNumber);
          if (table && !table.customers.includes(customerId)) {
            table.customers.push(customerId);
            table.status = 'ocupada';
            table.occupiedSince = table.occupiedSince ?? Date.now();
          }
          this.emit();
        },
      });
      this.demoOpenTable(tableNumber);
    });

    this.processor.on('table.open', (e) => {
      if (e.type !== 'table.open') {
        return;
      }
      const table = this.getTable(e.payload.tableNumber);
      if (table && table.status === 'libre') {
        table.status = 'reservada';
      }
    });

    this.processor.on('table.assign', (e) => {
      if (e.type !== 'table.assign') {
        return;
      }
      const table = this.getTable(e.payload.tableNumber);
      if (table) {
        table.waiterId = e.payload.waiterId;
      }
      const emp = this.employees.find((x) => x.id === e.payload.waiterId);
      if (emp) {
        emp.status = 'atendiendo';
        emp.activity = `Mesa ${e.payload.tableNumber}`;
      }
    });

    this.processor.on('order.register', (e) => {
      if (e.type !== 'order.register') {
        return;
      }
      const table = this.getTable(e.payload.tableNumber);
      if (!table) {
        return;
      }
      this.orderCounter += 1;
      const orderId = `ord-${this.orderCounter}`;
      table.orderId = orderId;
      table.orderItems = e.payload.items;
      table.consumptionTotal = e.payload.items.reduce(
        (sum, i) => sum + i.quantity * i.unitPrice,
        table.consumptionTotal,
      );
      this.kitchen.push({
        orderId,
        tableNumber: table.number,
        status: 'pendiente',
        items: e.payload.items.map((i) => ({ name: i.name, quantity: i.quantity })),
        createdAt: Date.now(),
      });
    });

    this.processor.on('order.send_kitchen', (e) => {
      if (e.type !== 'order.send_kitchen') {
        return;
      }
      const order = this.kitchen.find((k) => k.orderId === e.payload.orderId);
      if (order && order.status === 'pendiente') {
        order.status = 'en_preparacion';
        order.startedAt = Date.now();
      }
    });

    this.processor.on('order.preparing', (e) => {
      if (e.type !== 'order.preparing') {
        return;
      }
      const order = this.kitchen.find((k) => k.orderId === e.payload.orderId);
      if (order) {
        order.status = 'en_preparacion';
        order.startedAt = order.startedAt ?? Date.now();
      }
    });

    this.processor.on('order.ready', (e) => {
      if (e.type !== 'order.ready') {
        return;
      }
      const order = this.kitchen.find((k) => k.orderId === e.payload.orderId);
      if (order) {
        order.status = 'terminado';
        order.finishedAt = Date.now();
      }
    });

    this.processor.on('order.deliver', (e) => {
      if (e.type !== 'order.deliver') {
        return;
      }
      const order = this.kitchen.find((k) => k.orderId === e.payload.orderId);
      if (order) {
        order.status = 'entregado';
      }
      const emp = this.employees.find((x) => x.id === e.payload.waiterId);
      if (emp) {
        emp.activity = `Entrega mesa ${order?.tableNumber ?? '?'}`;
      }
    });

    this.processor.on('bill.request', (e) => {
      if (e.type !== 'bill.request') {
        return;
      }
      const table = this.getTable(e.payload.tableNumber);
      if (table) {
        table.status = 'cuenta_solicitada';
      }
    });

    this.processor.on('payment.collect', (e) => {
      if (e.type !== 'payment.collect') {
        return;
      }
      const { tableNumber, amount, method } = e.payload;
      this.register.salesToday += amount;
      this.register.totalCollected += amount;
      this.register.operationCount += 1;
      this.register.payments[method] = (this.register.payments[method] ?? 0) + amount;
      const table = this.getTable(tableNumber);
      if (table) {
        table.consumptionTotal = 0;
      }
    });

    this.processor.on('customer.leave', (e) => {
      if (e.type !== 'customer.leave') {
        return;
      }
      const { tableNumber, customerIds } = e.payload;
      for (const id of customerIds) {
        const ctrl = this.controllers.get(id);
        if (ctrl) {
          ctrl.followPath(pathFromTableToEntrance(tableNumber), {
            speed: 2.6,
            onComplete: () => {
              this.controllers.delete(id);
              this.emit();
            },
          });
          ctrl.setState('leaving', 'walk');
        }
      }
      const table = this.getTable(tableNumber);
      if (table) {
        table.customers = [];
      }
    });

    this.processor.on('table.release', (e) => {
      if (e.type !== 'table.release') {
        return;
      }
      const table = this.getTable(e.payload.tableNumber);
      if (table) {
        table.status = 'libre';
        table.waiterId = undefined;
        table.orderId = undefined;
        table.orderItems = [];
        table.occupiedSince = undefined;
      }
      for (const emp of this.employees) {
        if (emp.status === 'atendiendo') {
          emp.status = 'disponible';
          emp.activity = 'En salón';
        }
      }
    });

    this.processor.on('supplier.receive', (e) => {
      if (e.type !== 'supplier.receive') {
        return;
      }
      for (const item of e.payload.items) {
        const row = this.storage.find((s) => s.sku === item.sku);
        if (row) {
          row.quantity += item.quantity;
        } else {
          this.storage.push({
            sku: item.sku,
            name: item.name,
            quantity: item.quantity,
            minStock: 5,
          });
        }
      }
      this.lastSupplierDelivery = {
        at: Date.now(),
        supplierName: e.payload.supplierName,
        items: e.payload.items.map((i) => ({
          sku: i.sku,
          name: i.name,
          quantity: i.quantity,
          minStock: 0,
        })),
      };
    });

    this.processor.on('stock.update', (e) => {
      if (e.type !== 'stock.update') {
        return;
      }
      const row = this.storage.find((s) => s.sku === e.payload.sku);
      if (row) {
        row.quantity += e.payload.quantityDelta;
      }
    });
  }

  private bootstrapStaff(): void {
    for (const [id, pos] of Object.entries(WAITER_HOME)) {
      const emp = this.employees.find((e) => e.id === id);
      this.controllers.set(
        id,
        new CharacterController({
          id,
          type: 'waiter',
          displayName: emp?.name ?? id,
          position: { ...pos },
          state: 'idle',
          animation: 'idle',
        }),
      );
    }
  }

  private getTable(n: number): TableState | undefined {
    return this.tables.find((t) => t.number === n);
  }

  private findFreeTable(): number | undefined {
    const t = this.tables.find((x) => x.status === 'libre' || x.status === 'reservada');
    return t?.number;
  }

  private emit(): void {
    const snap = this.snapshot();
    for (const l of this.listeners) {
      l(snap);
    }
  }
}
